import React, { useContext, useEffect } from 'react';

import { PAYMENT_STRATEGY_OPTIONS } from 'components/Pages/Admin/Itinerary/Costs/constants';
import { getFragmentsFromTextBlock } from 'components/Pages/Admin/shared/PDFPreview/Pages/OtherPages/functions';
import { LANDING_PAGE_ROLES } from 'constants/index';
import i18n from 'i18n';

import { EBlockType } from '../../../__blocks';
import { PDFDataContext } from '../../context';
import { useRecursive } from '../../functions';
import { getPageTitle } from '../OtherPages';
import { MemoizedRecursivePage } from '../Page';
import { BLOCK_ROLE } from '../Page/Blocks/Text';

function SafariCosts({ onReady = () => {} }) {
  const { page, showImages } = useContext(PDFDataContext);
  const pageJSON = page.landing.pages.find((p) => p.role === LANDING_PAGE_ROLES.SAFARI_COSTS);
  const title = getPageTitle(pageJSON, page.settings) || 'Safari Costs';
  const landingPage = page?.landing?.pages.find(({ role }) => role === EBlockType.Costs);
  const image = landingPage?.primaryImageVersion800 || landingPage?.primaryImageUrl;

  const {
    type,
    guestCount,
    value,
    currency,
    roomingConfigurationEnabled,
    roomingConfigurationWysiwyg,
    accommodationType,
    notesEnabled,
    notesWysiwyg,
    includesWysiwyg,
    notIncludesWysiwyg,
    determined,
    topTextEnabled = true,
  } = page.costs;
  const costsCurrency = page.costsCurrency;
  const costType = PAYMENT_STRATEGY_OPTIONS.find((p) => p.value === type)?.value;

  const items = [
    ...(topTextEnabled && (determined || value)
      ? [
          {
            type: 'totalCosts',
            data: {
              topTextEnabled,
              type: costType,
              guestCount,
              value,
              currency,
              costsCurrency,
              accommodationType,
              determined,
            },
          },
        ]
      : []),
    ...(roomingConfigurationEnabled && roomingConfigurationWysiwyg?.data?.text
      ? getBlocksFromText(roomingConfigurationWysiwyg, {})
      : []),
    ...(notesEnabled && notesWysiwyg?.data?.text
      ? getBlocksFromText(notesWysiwyg, { title: `${i18n.t('proposal_content.costs.notes')}:` })
      : []),
    ...(!!includesWysiwyg && includesWysiwyg?.data?.text
      ? [
          { type: 'divider' },
          ...getBlocksFromText(includesWysiwyg, { title: `${i18n.t('proposal_content.costs.includes')}:` }),
        ]
      : []),
    ...(!!notIncludesWysiwyg && notIncludesWysiwyg?.data?.text
      ? getBlocksFromText(notIncludesWysiwyg, { title: `${i18n.t('proposal_content.costs.excludes')}:` })
      : []),
  ];

  const itemsWithImages = items.map((block) => ({
    ...block,
    image,
    primaryImageCoords: landingPage?.primaryImageCoords,
  }));

  const { innerPages, handleReady, handleFail } = useRecursive({ items: itemsWithImages, onReady });

  const showCondition = !pageJSON || pageJSON.hidden || items.length === 0;

  useEffect(() => {
    if (showCondition) {
      onReady();
    }
  }, [items]);

  if (showCondition) {
    return null;
  }

  return (
    <>
      {innerPages.map((p, i) => (
        <MemoizedRecursivePage
          key={i}
          blocks={p}
          onFail={handleFail}
          onReady={handleReady}
          title={title || p?.[0]?.pageTitle}
          isLastPage={i === innerPages.length - 1}
          pageNum={i}
          hasImageInPageTitle={showImages && !!image && i === 0}
        />
      ))}
    </>
  );
}

export default SafariCosts;

function getBlocksFromText(text, { title }) {
  const paragraphs = getFragmentsFromTextBlock(text, true);

  return paragraphs.map((p, i) => {
    if (i === 0 && title) {
      return {
        ...p,
        data: {
          ...p?.data,
          title,
        },

        role: paragraphs.length > 1 ? BLOCK_ROLE.LINE : BLOCK_ROLE.PARAGRAPH,
      };
    }

    return { ...p, role: i < paragraphs.length - 1 ? BLOCK_ROLE.LINE : BLOCK_ROLE.PARAGRAPH };
  });
}
